import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Drawer } from '@ahmdigital/ui';
import { Formik } from 'formik';
import { isEmpty } from 'lodash/fp';
import { useRef } from 'react';
import scrollToRef from '@ahmdigital/logic/lib/utils/scroll-to-ref';
import styled from 'styled-components';

import { DRAWER_IDS } from '../../ahm-constants/ui';
import { refinePriceSchema, type RefinePriceSchemaType } from '../../features/refine-price/schema';
import { RootState } from '../../reducers';
import CustomerActions from '../../actions/customer';
import CustomerForm from './components/customer-form';
import DrawerActions, { DrawerHydrationState } from '../../actions/drawer';
import Footer from './components/footer';
import selectCurrentDrawer from '../../selectors/select-current-drawer';
import selectCustomer from '../../selectors/select-customer';
import selectCustomerFormInitialValues from './selectors/select-customer-form-initial-values';
import submitRefinePrice from '../../pages/refine-price/utils/submit-refine-price';
import type { Customer } from '../../selectors/select-customer/types';
import type { Match, PricingDrawerActions } from './types';
import type { ProductType } from '../../types/product';

type PricingDrawerProps = {
  actions: PricingDrawerActions;
  customer: Customer;
  drawerId: string | null;
  drawerHydrationState: DrawerHydrationState | null;
  initialValues: RefinePriceSchemaType;
  isProductWithHospitalServices: boolean;
  match?: Match;
  product?: ProductType;
  showIncomeOptions?: boolean;
};

const StyledScrollMarginDiv = styled.div`
  scroll-margin-top: 5rem;
`;

const PricingDrawer = ({
  actions,
  customer,
  drawerId = null,
  drawerHydrationState,
  initialValues,
  isProductWithHospitalServices,
  match = undefined,
  showIncomeOptions = true,
  product = {} as ProductType,
}: PricingDrawerProps) => {
  const formRef = useRef<HTMLDivElement>(null);
  const closeDrawer = () => {
    actions.drawer.closeDrawer();
  };

  const drawerFooter = (
    <Footer handleClose={closeDrawer} isProductWithHospitalServices={isProductWithHospitalServices} />
  );
  const isOpen = drawerId === DRAWER_IDS.CHANGE_DETAILS;
  const hydrationStateForDrawer = drawerHydrationState?.id === DRAWER_IDS.CHANGE_DETAILS ? drawerHydrationState : null;
  const formInitialValues = (hydrationStateForDrawer?.initialValues ?? initialValues) as RefinePriceSchemaType;
  const formInitialErrors = hydrationStateForDrawer?.initialErrors;
  const formInitialTouched = hydrationStateForDrawer?.initialTouched;

  return (
    <Formik<RefinePriceSchemaType>
      enableReinitialize
      validationSchema={refinePriceSchema}
      initialErrors={formInitialErrors}
      initialTouched={formInitialTouched}
      initialValues={formInitialValues}
      onSubmit={(values) => {
        submitRefinePrice({ actions, customer, formValues: values, initialValues });
      }}
    >
      {({ errors, isSubmitting, submitCount, touched }) => {
        if (isSubmitting && !isEmpty(errors)) {
          scrollToRef(formRef, { behavior: 'smooth' });
        }
        return (
          <Drawer title="Change details" isOpen={isOpen} toggleMenu={actions.drawer.closeDrawer} footer={drawerFooter}>
            <StyledScrollMarginDiv ref={formRef}>
              <CustomerForm
                isProductWithHospitalServices={isProductWithHospitalServices}
                hasSubmitAttempt={Boolean(submitCount) || !isEmpty(touched)}
                match={match}
                product={product}
                showIncomeOptions={showIncomeOptions}
              />
            </StyledScrollMarginDiv>
          </Drawer>
        );
      }}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => ({
  customer: selectCustomer(state),
  drawerHydrationState: state.drawer.drawerHydrationState,
  drawerId: selectCurrentDrawer(state),
  initialValues: selectCustomerFormInitialValues(state),
  scale: selectCustomer(state).scale,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
  actions: {
    customer: bindActionCreators(CustomerActions, dispatch),
    drawer: bindActionCreators(DrawerActions, dispatch),
  },
});

// @ts-expect-error - Automatic, Please fix when editing this file
export default connect(mapStateToProps, mapDispatchToProps)(PricingDrawer);

export { PricingDrawer as PricingDrawerForTest };
